import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';

import { gtmEvent } from '@dtvgo/gtm-event-report';

import CommunicationAdLink from '../../components/communicationAdLink/CommunicationAdLink';
import { useGtmContext } from '../../components/GtmContext';
import { formatBannerTitle, gtmCommunicationDimensions } from '../../components/GtmContext/dimensions/communication';
import { getCommunicationCarousel } from '../../services/communicationCarousel';
import { useImpressionOnVisible } from '../../hooks/useImpressionOnVisible';
import { getLanderNameByCaption } from '../../utils';
import { mapHomeBannerPayload } from './mapHomeBannerPayload';
import {
  STORAGE_TOAST_BANNER_DISMISS_PREFIX,
} from '../../utils/constants';

import './ToastBannerSlot.scss';

const PLACEHOLDER_ASSET = { __toastBannerPlaceholder: true };

function formatYmdInTimeZone(date, timeZone) {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function isDismissedToday(carouselId, timeZone) {
  const key = `${STORAGE_TOAST_BANNER_DISMISS_PREFIX}${carouselId}`;
  const saved = localStorage.getItem(key);
  if (!saved) return false;
  return saved === formatYmdInTimeZone(new Date(), timeZone);
}

function persistDismissToday(carouselId, timeZone) {
  const key = `${STORAGE_TOAST_BANNER_DISMISS_PREFIX}${carouselId}`;
  localStorage.setItem(key, formatYmdInTimeZone(new Date(), timeZone));
}

function ToastBannerSlot({
  carouselId,
  onFetched,
  fetchEnabled,
}) {
  const { timeZone } = useIntl();
  const { gtmUserData } = useGtmContext();
  const { params: { landerUri, pageId } } = useRouteMatch();
  const landerTab = getLanderNameByCaption(landerUri || pageId);
  const onFetchedRef = useRef(onFetched);
  const [banner, setBanner] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const [visible, setVisible] = useState(true);

  onFetchedRef.current = onFetched;

  const notifyFetched = useCallback(() => {
    onFetchedRef.current([PLACEHOLDER_ASSET]);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!fetchEnabled) {
      queueMicrotask(() => {
        if (!cancelled) notifyFetched();
      });
      return () => {
        cancelled = true;
      };
    }

    if (isDismissedToday(carouselId, timeZone)) {
      queueMicrotask(() => {
        if (!cancelled) notifyFetched();
      });
      return () => {
        cancelled = true;
      };
    }

    setBanner(null);

    (async () => {
      const raw = await getCommunicationCarousel({ carouselId });
      if (cancelled) return;
      setBanner(mapHomeBannerPayload(raw));
      notifyFetched();
    })();

    return () => {
      cancelled = true;
    };
  }, [carouselId, fetchEnabled, notifyFetched, timeZone]);

  const bannerRef = useImpressionOnVisible({
    enabled: Boolean(banner) && visible,
    resetKey: carouselId,
    onImpression: () => {
      const { hash, eventType, dimensions } = gtmCommunicationDimensions.dialog_banner.page_view;
      gtmEvent({
        hash,
        eventType,
        dimensions,
        userData: { ...gtmUserData, v_banner_title: formatBannerTitle(banner.title) },
        data: { tab: landerTab },
      });
    },
  });

  const handleBannerClick = useCallback(() => {
    const { hash, eventType, dimensions } = gtmCommunicationDimensions.dialog_banner.banner_click;
    gtmEvent({
      hash,
      eventType,
      dimensions,
      userData: { ...gtmUserData, v_banner_title: formatBannerTitle(banner?.title) },
      data: { tab: landerTab },
    });
  }, [gtmUserData, banner?.title, landerTab]);

  const handleDismiss = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    persistDismissToday(carouselId, timeZone);
    setVisible(false);
  }, [carouselId, timeZone]);

  const handleLogoError = useCallback(() => {
    setLogoFailed(true);
  }, []);

  if (!banner || !visible) {
    return null;
  }

  const hasImage = Boolean(banner.backgroundUrl);
  const hasLogo = Boolean(banner.logoUrl) && !logoFailed;
  const hasCtaLink = Boolean(banner.ctaLink);
  const hasCtaLabel = Boolean(banner.ctaText);
  const linkAriaLabel = banner.title || 'Promotional banner';

  const containerStyle = {};
  if (hasImage) {
    containerStyle.backgroundImage = `url(${banner.backgroundUrl})`;
  } else if (banner.backgroundColor) {
    containerStyle.backgroundColor = banner.backgroundColor;
  }

  return (
    <div className="dtv-toast-banner-overlay" role="presentation">
      <section
        ref={bannerRef}
        className={classNames('dtv-toast-banner', {
          'dtv-toast-banner--with-image': hasImage,
          'dtv-toast-banner--solid': !hasImage,
          'dtv-toast-banner--is-link': hasCtaLink,
        })}
        style={containerStyle}
        role="dialog"
        aria-modal="false"
        aria-label={linkAriaLabel}
      >
        {hasImage && <div className="dtv-toast-banner__overlay" aria-hidden="true" />}
        <div className="dtv-toast-banner__row">
          <div className="dtv-toast-banner__brand">
            {hasLogo && (
              <img
                src={banner.logoUrl}
                alt=""
                className="dtv-toast-banner__logo"
                onError={handleLogoError}
              />
            )}
            {banner.title && (
              <h3 className="dtv-toast-banner__title">{banner.title}</h3>
            )}
          </div>
          {hasCtaLabel && (
            <span className="dtv-toast-banner__cta-link">
              {banner.ctaText}
            </span>
          )}
        </div>
        {hasCtaLink && (
          <CommunicationAdLink
            href={banner.ctaLink}
            className="dtv-toast-banner__hit-area"
            ariaLabel={linkAriaLabel}
            tabIndex={-1}
            onBeforeNavigate={handleBannerClick}
          >
            <span className="dtv-toast-banner__hit-area-text">{linkAriaLabel}</span>
          </CommunicationAdLink>
        )}
        <button
          type="button"
          className="dtv-toast-banner__close"
          aria-label="Close"
          onClick={handleDismiss}
        >
          <svg
            className="dtv-toast-banner__close-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              d="M6 6l12 12M18 6L6 18"
            />
          </svg>
        </button>
      </section>
    </div>
  );
}

ToastBannerSlot.propTypes = {
  carouselId: PropTypes.string.isRequired,
  onFetched: PropTypes.func.isRequired,
  fetchEnabled: PropTypes.bool,
};

ToastBannerSlot.defaultProps = {
  fetchEnabled: true,
};

export default ToastBannerSlot;
